import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Modal,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Inventory as InventoryIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ResponsiveContainer,
} from 'recharts';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [zeroStockProducts, setZeroStockProducts] = useState([]);
  const [loadingZeroStock, setLoadingZeroStock] = useState(false);

  // Edit stock states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    mjtechStock: 0,
    mobileCareStock: 0,
  });
  const [updating, setUpdating] = useState(false);

  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/dashboard/stats');
      setStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchZeroStockProducts = async () => {
    try {
      setLoadingZeroStock(true);
      const response = await api.get('/api/v1/dashboard/zero-stock-products');
      setZeroStockProducts(response.data || []);
      setModalOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingZeroStock(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleEditClick = (product) => {
    console.log('Selected product:', product);
    console.log('Product keys:', Object.keys(product));

    // Extract the ID - iterate through keys to find it
    let productId = null;
    const keys = Object.keys(product);

    // First try direct access
    if (product.ID) {
      productId = product.ID;
    } else if (product.id) {
      productId = product.id;
    } else {
      // If direct access fails, iterate through keys
      for (const key of keys) {
        if (key.toLowerCase() === 'id') {
          productId = product[key];
          console.log(`Found ID with key: "${key}"`);
          break;
        }
      }
    }

    console.log('Extracted product ID:', productId);
    console.log('Type of product ID:', typeof productId);

    const mjtechStock = product.Stock?.find(s => s.BranchName === 'MJTech')?.Quantity || 0;
    const mobileCareStock = product.Stock?.find(s => s.BranchName === 'Mobile Care')?.Quantity || 0;

    console.log('MJTech stock:', mjtechStock);
    console.log('Mobile Care stock:', mobileCareStock);

    setSelectedProduct(product);
    setSelectedProductId(productId);
    setEditFormData({
      mjtechStock,
      mobileCareStock,
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProduct(null);
    setSelectedProductId(null);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleFormChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0) {
      setEditFormData(prev => ({
        ...prev,
        [field]: numValue,
      }));
    }
  };

  const handleUpdateStock = () => {
    setEditDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    setUpdating(true);
    setConfirmDialogOpen(false);

    try {
      console.log('Using stored product ID:', selectedProductId);
      console.log('MJTech stock:', editFormData.mjtechStock);
      console.log('Mobile Care stock:', editFormData.mobileCareStock);

      if (!selectedProductId) {
        throw new Error('Product ID is missing');
      }

      // Update MJTech stock
      await api.put(`/api/v1/products/${selectedProductId}/stock/MJTech`, {
        Quantity: editFormData.mjtechStock,
        MinStockLevel: 0,
        MaxStockLevel: 0,
      });

      // Update Mobile Care stock
      await api.put(`/api/v1/products/${selectedProductId}/stock/Mobile Care`, {
        Quantity: editFormData.mobileCareStock,
        MinStockLevel: 0,
        MaxStockLevel: 0,
      });

      setSnackbar({
        open: true,
        message: `Stock updated successfully for ${selectedProduct.Brand} ${selectedProduct.Model}`,
        severity: 'success',
      });

      // Refresh the zero stock products list
      await fetchZeroStockProducts();

      // Refresh dashboard stats
      await fetchDashboardStats();
    } catch (err) {
      console.error('Error updating stock:', err);
      console.error('Error response:', err.response?.data);
      setSnackbar({
        open: true,
        message: `Failed to update stock: ${err.response?.data?.Error || err.message}`,
        severity: 'error',
      });
    } finally {
      setUpdating(false);
      setSelectedProduct(null);
      setSelectedProductId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const StatCard = ({ icon, title, value, subtitle, color, valueColor, progress, onClick }) => (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${valueColor || color} 0%, ${valueColor || color}80 100%)`,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          flexDirection: { xs: isSmallMobile ? 'column' : 'row', sm: 'row' },
          textAlign: { xs: isSmallMobile ? 'center' : 'left', sm: 'left' },
          gap: { xs: isSmallMobile ? 1 : 0, sm: 0 }
        }}>
          <Box
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              background: `${valueColor || color}20`,
              border: `1px solid ${valueColor || color}40`,
              mr: { xs: isSmallMobile ? 0 : 2, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color: valueColor || color, fontSize: { xs: 24, sm: 28 } } })}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, justifyContent: { xs: isSmallMobile ? 'center' : 'flex-start', sm: 'flex-start' } }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${valueColor || color} 30%, ${valueColor || color}80 90%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
        </Box>
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: `${valueColor || color}20`,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: valueColor || color,
                  borderRadius: 3,
                },
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {progress}% capacity
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const formatCurrency = (amount) => {
    // Force PHP/Peso currency formatting
    return '₱' + new Intl.NumberFormat('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Prepare chart data
  const salesTrendData = [
    {
      period: 'Today',
      sales: stats?.SalesAnalytics?.TodaySales?.TotalSales || 0,
      transactions: stats?.SalesAnalytics?.TodaySales?.TransactionCount || 0,
    },
    {
      period: 'Monthly',
      sales: stats?.SalesAnalytics?.MonthlySales?.TotalSales || 0,
      transactions: stats?.SalesAnalytics?.MonthlySales?.TransactionCount || 0,
    },
    {
      period: 'Yearly',
      sales: stats?.SalesAnalytics?.YearlySales?.TotalSales || 0,
      transactions: stats?.SalesAnalytics?.YearlySales?.TransactionCount || 0,
    },
  ];

  const stockDistributionData = [
    {
      name: 'MJTech Stock',
      value: stats?.MjtechStock || 0,
      color: theme.palette.info.main,
    },
    {
      name: 'Mobile Care Stock',
      value: stats?.MobileCareStock || 0,
      color: theme.palette.success.main,
    },
  ];

  const inventoryStatusData = [
    {
      name: 'Total Products',
      value: stats?.TotalProducts || 0,
      color: theme.palette.primary.main,
    },
    {
      name: 'Total Brands',
      value: stats?.TotalBrands || 0,
      color: theme.palette.secondary.main,
    },
    {
      name: 'Low Stock Items',
      value: stats?.LowStockCount || 0,
      color: theme.palette.warning.main,
    },
  ];

  // Chart colors
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          System Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Real-time inventory overview and key metrics
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Total Products */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<CategoryIcon />}
            title="Total Products"
            value={stats?.TotalProducts || 0}
            subtitle="Active product catalog"
            color={theme.palette.primary.main}
          />
        </Grid>

        {/* Total Brands */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<StoreIcon />}
            title="Total Brands"
            value={stats?.TotalBrands || 0}
            subtitle="Brand portfolio"
            color={theme.palette.secondary.main}
            valueColor={theme.palette.primary.main}
          />
        </Grid>

        {/* Zero Stock Alert */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<WarningIcon />}
            title="Zero Stock Items"
            value={stats?.ZeroStockCount || 0}
            subtitle="Click to view details"
            color={theme.palette.warning.main}
            valueColor={theme.palette.primary.main}
            onClick={fetchZeroStockProducts}
          />
        </Grid>

        {/* MJTech Stock */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<InventoryIcon />}
            title="MJTech Stock"
            value={stats?.MjtechStock || 0}
            subtitle="Main branch inventory"
            color={theme.palette.info.main}
          />
        </Grid>

        {/* Mobile Care Stock */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<InventoryIcon />}
            title="Mobile Care Stock"
            value={stats?.MobileCareStock || 0}
            subtitle="Branch inventory"
            color={theme.palette.success.main}
            valueColor={theme.palette.primary.main}
          />
        </Grid>

        {/* Total Stock Value */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<AccountBalanceIcon />}
            title="Total Stock Value"
            value={formatCurrency(stats?.TotalStockValue || 0)}
            subtitle="Inventory valuation"
            color={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      {/* Sales Analytics Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3,
          }}
        >
          Sales Analytics
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* COLUMN 1: Sales */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<TodayIcon />}
              title="Today's Sales"
              value={formatCurrency(stats?.SalesAnalytics?.TodaySales?.TotalSales || 0)}
              subtitle={`${stats?.SalesAnalytics?.TodaySales?.TransactionCount || 0} transactions`}
              color={theme.palette.primary.main}
              valueColor={theme.palette.primary.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<ShoppingCartIcon />}
              title="Monthly Sales"
              value={formatCurrency(stats?.SalesAnalytics?.MonthlySales?.TotalSales || 0)}
              subtitle={`${stats?.SalesAnalytics?.MonthlySales?.TransactionCount || 0} transactions`}
              color={theme.palette.primary.main}
              valueColor={theme.palette.primary.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<ReceiptIcon />}
              title="Yearly Sales"
              value={formatCurrency(stats?.SalesAnalytics?.YearlySales?.TotalSales || 0)}
              subtitle={`${stats?.SalesAnalytics?.YearlySales?.TransactionCount || 0} transactions`}
              color={theme.palette.primary.main}
              valueColor={theme.palette.primary.main}
            />
          </Grid>

          {/* COLUMN 2: Capital */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<SavingsIcon />}
              title="Today's Capital"
              value={formatCurrency(stats?.SalesAnalytics?.TodaySales?.TotalCapital || 0)}
              subtitle="Investment cost"
              color={theme.palette.warning.main}
              valueColor={theme.palette.warning.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<SavingsIcon />}
              title="Monthly Capital"
              value={formatCurrency(stats?.SalesAnalytics?.MonthlySales?.TotalCapital || 0)}
              subtitle="30 days investment"
              color={theme.palette.warning.main}
              valueColor={theme.palette.warning.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<SavingsIcon />}
              title="Yearly Capital"
              value={formatCurrency(stats?.SalesAnalytics?.YearlySales?.TotalCapital || 0)}
              subtitle="365 days investment"
              color={theme.palette.warning.main}
              valueColor={theme.palette.warning.main}
            />
          </Grid>

          {/* COLUMN 3: Profit */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Today's Profit"
              value={formatCurrency(stats?.SalesAnalytics?.TodaySales?.TotalProfit || 0)}
              subtitle={`${(stats?.SalesAnalytics?.TodaySales?.ProfitMargin || 0).toFixed(1)}% margin`}
              color={theme.palette.success.main}
              valueColor={theme.palette.success.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Monthly Profit"
              value={formatCurrency(stats?.SalesAnalytics?.MonthlySales?.TotalProfit || 0)}
              subtitle={`${(stats?.SalesAnalytics?.MonthlySales?.ProfitMargin || 0).toFixed(1)}% margin`}
              color={theme.palette.success.main}
              valueColor={theme.palette.success.main}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Yearly Profit"
              value={formatCurrency(stats?.SalesAnalytics?.YearlySales?.TotalProfit || 0)}
              subtitle={`${(stats?.SalesAnalytics?.YearlySales?.ProfitMargin || 0).toFixed(1)}% margin`}
              color={theme.palette.success.main}
              valueColor={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* Sales Details */}
        <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Sales Performance Details
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      Today's Performance
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average Order Value: {formatCurrency(stats?.SalesAnalytics?.TodaySales?.AverageOrderValue || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Items Sold: {stats?.SalesAnalytics?.TodaySales?.TotalQuantity || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Capital: {formatCurrency(stats?.SalesAnalytics?.TodaySales?.TotalCapital || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Profit: {formatCurrency(stats?.SalesAnalytics?.TodaySales?.TotalProfit || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      Monthly Performance (30 days)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average Order Value: {formatCurrency(stats?.SalesAnalytics?.MonthlySales?.AverageOrderValue || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Items Sold: {stats?.SalesAnalytics?.MonthlySales?.TotalQuantity || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Capital: {formatCurrency(stats?.SalesAnalytics?.MonthlySales?.TotalCapital || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Profit: {formatCurrency(stats?.SalesAnalytics?.MonthlySales?.TotalProfit || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      Yearly Performance (365 days)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average Order Value: {formatCurrency(stats?.SalesAnalytics?.YearlySales?.AverageOrderValue || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Items Sold: {stats?.SalesAnalytics?.YearlySales?.TotalQuantity || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Capital: {formatCurrency(stats?.SalesAnalytics?.YearlySales?.TotalCapital || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Profit: {formatCurrency(stats?.SalesAnalytics?.YearlySales?.TotalProfit || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts and Analytics Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #9C27B0 30%, #E91E63 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3,
          }}
        >
          Visual Analytics & Trends
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Sales Trend Chart */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Sales Performance Trend
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
                  <BarChart data={salesTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === 'sales' ? formatCurrency(value) : value,
                        name === 'sales' ? 'Sales' : 'Transactions'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="sales" fill={theme.palette.primary.main} name="Sales Amount" />
                    <Bar dataKey="transactions" fill={theme.palette.secondary.main} name="Transaction Count" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Stock Distribution Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Stock Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
                  <PieChart>
                    <Pie
                      data={stockDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={isMobile ? 60 : 80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stockDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Inventory Overview Pie Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Inventory Overview
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <PieChart>
                    <Pie
                      data={inventoryStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {inventoryStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Sales Line Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Sales Progression
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <LineChart data={salesTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value) => [formatCurrency(value), 'Sales Amount']}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Additional Info Section */}
      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              System Status
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      mr: 2,
                      boxShadow: `0 0 10px ${theme.palette.success.main}40`,
                    }}
                  />
                  <Typography variant="body1">
                    Database Connection: <strong>Online</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      mr: 2,
                      boxShadow: `0 0 10px ${theme.palette.success.main}40`,
                    }}
                  />
                  <Typography variant="body1">
                    API Status: <strong>Operational</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      mr: 2,
                      boxShadow: `0 0 10px ${theme.palette.primary.main}40`,
                    }}
                  />
                  <Typography variant="body1">
                    Last Update: <strong>Real-time</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.info.main,
                      mr: 2,
                      boxShadow: `0 0 10px ${theme.palette.info.main}40`,
                    }}
                  />
                  <Typography variant="body1">
                    Version: <strong>v1.0.0 Beta</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Zero Stock Products Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="zero-stock-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)',
            }}
          >
            <Typography
              id="zero-stock-modal-title"
              variant="h5"
              sx={{ fontWeight: 700, color: 'white' }}
            >
              Products with Zero Stock
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Body */}
          <Box sx={{ p: 3, overflow: 'auto', flexGrow: 1 }}>
            {loadingZeroStock ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : zeroStockProducts.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                }}
              >
                <WarningIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No products with zero stock!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  All products have stock available.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop Table View */}
                {!isMobile && (
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Brand</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Model</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>MJTech Stock</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Mobile Care Stock</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {zeroStockProducts.map((product) => {
                          const mjtechStock = product.Stock?.find(s => s.BranchName === 'MJTech')?.Quantity || 0;
                          const mobileCareStock = product.Stock?.find(s => s.BranchName === 'Mobile Care')?.Quantity || 0;

                          return (
                            <TableRow
                              key={product.ID}
                              sx={{
                                '&:hover': { bgcolor: 'action.hover' },
                                '&:last-child td, &:last-child th': { border: 0 },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600 }}>{product.Brand}</TableCell>
                              <TableCell>{product.Model}</TableCell>
                              <TableCell>
                                <Chip
                                  label={product.PartCategory}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={mjtechStock}
                                  size="small"
                                  color={mjtechStock === 0 ? 'error' : 'success'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={mobileCareStock}
                                  size="small"
                                  color={mobileCareStock === 0 ? 'error' : 'success'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(product)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white',
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Mobile Card View */}
                {isMobile && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {zeroStockProducts.map((product) => {
                      const mjtechStock = product.Stock?.find(s => s.BranchName === 'MJTech')?.Quantity || 0;
                      const mobileCareStock = product.Stock?.find(s => s.BranchName === 'Mobile Care')?.Quantity || 0;

                      return (
                        <Card
                          key={product.ID}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { boxShadow: 3 },
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {product.Brand}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  {product.Model}
                                </Typography>
                                <Chip
                                  label={product.PartCategory}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(product)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'white',
                                  },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                  MJTech Stock
                                </Typography>
                                <Chip
                                  label={mjtechStock}
                                  size="small"
                                  color={mjtechStock === 0 ? 'error' : 'success'}
                                  sx={{ width: '100%' }}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Mobile Care Stock
                                </Typography>
                                <Chip
                                  label={mobileCareStock}
                                  size="small"
                                  color={mobileCareStock === 0 ? 'error' : 'success'}
                                  sx={{ width: '100%' }}
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Modal Footer */}
          {!loadingZeroStock && zeroStockProducts.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="body2" color="textSecondary" align="center">
                Total products with zero stock: <strong>{zeroStockProducts.length}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Stock Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          color: 'white',
          fontWeight: 700,
        }}>
          Edit Stock - {selectedProduct?.Brand} {selectedProduct?.Model}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="MJTech Stock"
                type="number"
                value={editFormData.mjtechStock}
                onChange={(e) => handleFormChange('mjtechStock', e.target.value)}
                InputProps={{
                  inputProps: { min: 0 },
                }}
                helperText="Enter the new stock quantity for MJTech branch"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile Care Stock"
                type="number"
                value={editFormData.mobileCareStock}
                onChange={(e) => handleFormChange('mobileCareStock', e.target.value)}
                InputProps={{
                  inputProps: { min: 0 },
                }}
                helperText="Enter the new stock quantity for Mobile Care branch"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleEditDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStock}
            variant="contained"
            color="primary"
          >
            Update Stock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)',
          color: 'white',
          fontWeight: 700,
        }}>
          Confirm Stock Update
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Update stock for {selectedProduct?.Brand} {selectedProduct?.Model}?
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Stock Changes:
            </Typography>

            <Box sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              mb: 2,
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    MJTech Stock
                  </Typography>
                  <Typography variant="h6">
                    {selectedProduct?.Stock?.find(s => s.BranchName === 'MJTech')?.Quantity || 0}
                    <Typography component="span" color="primary" sx={{ mx: 1 }}>
                      →
                    </Typography>
                    {editFormData.mjtechStock}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Mobile Care Stock
                  </Typography>
                  <Typography variant="h6">
                    {selectedProduct?.Stock?.find(s => s.BranchName === 'Mobile Care')?.Quantity || 0}
                    <Typography component="span" color="primary" sx={{ mx: 1 }}>
                      →
                    </Typography>
                    {editFormData.mobileCareStock}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              This action will update the stock levels and create stock movement records.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleConfirmDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            color="warning"
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Confirm Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;